<div class="mx-auto max-w-md p-3 sm:p-4">
  <mat-card
    class="overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-sm"
  >
    <div class="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
      <button
        mat-icon-button
        type="button"
        class="!flex !h-9 !w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100"
        (click)="goHome()"
        aria-label="Go back"
      >
        <lucide-icon name="arrow-left" class="h-5 w-5"></lucide-icon>
      </button>
      <h1 class="text-xl font-semibold text-gray-900">Settings</h1>
    </div>

    <div class="flex flex-col gap-3 p-4">
      <section class="rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm">
        <div class="flex items-center justify-between gap-3">
          <div class="flex min-w-0 items-center gap-3">
            <div
              class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700/80 ring-1 ring-amber-100"
            >
              <lucide-icon name="monitor" class="h-5 w-5"></lucide-icon>
            </div>
            <h2 class="text-sm font-semibold text-gray-900">Kiosk Mode</h2>
          </div>
          <button
            type="button"
            role="switch"
            class="apple-toggle"
            [attr.aria-checked]="isKioskMode"
            [class.apple-toggle--on]="isKioskMode"
            (click)="toggleKioskMode()"
          >
            <span class="apple-toggle__thumb" aria-hidden="true"></span>
          </button>
        </div>

        <p class="mt-3 text-xs leading-relaxed text-gray-500">
          In this mode, upon submitting the survey, the screen will automatically refresh to
          display the same survey, making it ready for immediate reuse.
        </p>

        <div *ngIf="isKioskMode" class="mt-3 border-t border-gray-100 pt-3">
          <button
            mat-raised-button
            color="warn"
            class="!rounded-lg !px-4 !text-xs !font-semibold !shadow-sm"
            (click)="clearData()"
          >
            Reset Data
          </button>
        </div>
      </section>

      <section
        *ngIf="isKioskMode"
        class="rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm"
      >
        <div class="flex items-center gap-3">
          <div
            class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700/80 ring-1 ring-sky-100"
          >
            <lucide-icon name="refresh-cw" class="h-5 w-5"></lucide-icon>
          </div>
          <h2 class="text-sm font-semibold text-gray-900">Offline Sync</h2>
        </div>

        <div class="mt-3 space-y-2">
          <div
            class="flex items-center justify-between rounded-lg bg-gray-50/80 px-3 py-2 text-xs ring-1 ring-gray-200/60"
          >
            <span class="text-gray-500">Offline cache</span>
            <span class="font-semibold text-gray-900">{{ offlineCacheLabel }}</span>
          </div>

          <p
            *ngIf="offlineCacheStatus === 'installing' || offlineCacheStatus === 'activating'"
            class="text-xs leading-relaxed text-amber-700"
          >
            Downloading app files. Keep this page open while online.
          </p>
          <p *ngIf="offlineCacheStatus === 'failed'" class="text-xs leading-relaxed text-red-600">
            Cache install failed. Reload while online or clear site data and try again.
          </p>
          <p
            *ngIf="offlineCacheStatus === 'not_registered'"
            class="text-xs leading-relaxed text-gray-500"
          >
            Offline cache is not ready yet. Stay online until installation completes.
          </p>

          <div
            class="flex items-center justify-between rounded-lg bg-gray-50/80 px-3 py-2 text-xs ring-1 ring-gray-200/60"
          >
            <span class="text-gray-500">Connection</span>
            <span
              class="inline-flex items-center gap-1.5 font-semibold"
              [ngClass]="isOnline ? 'text-green-600' : 'text-red-600'"
            >
              <span
                class="h-2 w-2 rounded-full"
                [ngClass]="isOnline ? 'bg-green-500' : 'bg-red-500'"
              ></span>
              {{ isOnline ? 'Online' : 'Offline' }}
            </span>
          </div>

          <p *ngIf="!isOnline" class="text-xs leading-relaxed text-gray-500">
            No internet detected. Completed surveys will be saved locally. Use Sync now when you
            are back online.
          </p>

          <div
            class="flex items-center justify-between rounded-lg bg-gray-50/80 px-3 py-2 text-xs ring-1 ring-gray-200/60"
          >
            <span class="text-gray-500">Last sync</span>
            <span class="font-semibold text-gray-900">{{ lastSyncLabel }}</span>
          </div>

          <div
            class="flex items-center justify-between gap-3 rounded-lg bg-gray-50/80 px-3 py-2 text-xs ring-1 ring-gray-200/60"
          >
            <div class="min-w-0">
              <span class="text-gray-500">Pending submissions</span>
              <span class="ml-1 font-semibold text-gray-900">{{ pendingSubmissionCount }}</span>
            </div>
            <button
              mat-raised-button
              color="primary"
              class="btn-primary !h-8 !min-w-0 flex-shrink-0 !rounded-lg !px-3.5 !py-0 !text-xs !font-semibold !shadow-sm"
              (click)="syncNow()"
              [disabled]="isSyncing || pendingSubmissionCount === 0 || !isOnline"
            >
              {{ isSyncing ? 'Syncing...' : 'Sync now' }}
            </button>
          </div>

          <div
            *ngIf="showTestMultiplier"
            class="flex items-center justify-between gap-3 rounded-lg bg-gray-50/80 px-3 py-2 text-xs ring-1 ring-gray-200/60"
          >
            <label class="min-w-0 text-gray-500" for="pending-multiplier">
              Test multiplier
            </label>
            <div class="flex flex-shrink-0 items-center gap-2">
              <input
                id="pending-multiplier"
                type="number"
                min="2"
                max="500"
                class="h-8 w-16 rounded-md border border-gray-300 px-2 text-right font-semibold text-gray-900"
                [(ngModel)]="pendingMultiplier"
                [disabled]="isSyncing || isMultiplying"
              />
              <button
                mat-stroked-button
                type="button"
                class="!h-8 !min-w-0 !rounded-lg !px-3 !py-0 !text-xs !font-semibold"
                (click)="multiplyPending()"
                [disabled]="
                  isSyncing || isMultiplying || pendingSubmissionCount === 0
                "
              >
                {{ isMultiplying ? '...' : 'Multiply' }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm">
        <div class="flex items-center gap-3">
          <div
            class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600 ring-1 ring-gray-200/80"
          >
            <lucide-icon name="info" class="h-5 w-5"></lucide-icon>
          </div>
          <h2 class="text-sm font-semibold text-gray-900">App Info</h2>
        </div>

        <div class="mt-3 space-y-2">
          <div
            class="flex items-center justify-between rounded-lg bg-gray-50/80 px-3 py-2 text-xs ring-1 ring-gray-200/60"
          >
            <span class="text-gray-500">Version</span>
            <span class="font-semibold text-gray-900">{{ version }}</span>
          </div>
          <div
            class="flex items-center justify-between rounded-lg bg-gray-50/80 px-3 py-2 text-xs ring-1 ring-gray-200/60"
          >
            <span class="text-gray-500">Environment</span>
            <span class="font-semibold text-gray-900">{{ env }}</span>
          </div>
          <div
            class="rounded-lg bg-gray-50/80 px-3 py-2 text-xs text-gray-500 ring-1 ring-gray-200/60"
          >
            Release Notes: <span class="font-medium text-gray-700">None</span>
          </div>
        </div>
      </section>
    </div>
  </mat-card>
</div>
